import React, { useState } from "react";
import {
  HolidayVillage as HolidayIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { GridValidRowModel } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { endOfYear, startOfYear } from "date-fns";
import { PageTitle, DataGrid, FilterBar } from "../../components";
import { useResponsive } from "../../hooks";
import { Country, UpdateBankHolidayInput } from "../../API";
import { useBankHolidayLib } from "../../lib";
import { columns, hiddenColumnsMobile } from "./columns";
import { useRequestModals } from "./Modals";
import { useBankHolidayQuery } from "./useBankHolidayQuery";

const TEST_ID = "bank-holidays";

export const BankHolidays = () => {
  const [dates, setDates] = useState<Interval | null>({
    start: startOfYear(new Date()),
    end: endOfYear(new Date()),
  });
  const [country, setCountry] = useState<Country | "">("");

  const { isMd } = useResponsive();

  const { updateBankHolidayRecord } = useBankHolidayLib();
  const { showCreateModal, showDeleteModal, showUpdateModal } =
    useRequestModals();

  const { enqueueSnackbar } = useSnackbar();
  const showSnackbar = (message: string, error = false) => {
    enqueueSnackbar(message, { variant: error ? "error" : "success" });
  };

  const { items, isError, refetch, isPending } = useBankHolidayQuery(
    dates,
    country
  );

  const handleRowUpdate = (row: GridValidRowModel) => {
    const { description, id, endDate, startDate } = row;
    const data = {
      description,
      id,
      endDate,
      startDate,
    } as UpdateBankHolidayInput;
    updateBankHolidayRecord(data, {
      onSuccess: () => postAction("Bank Holiday has been updated.", false),
      onError: () =>
        postAction(
          "Error updating Bank Holiday. Please try again in a few seconds",
          true
        ),
    });
    return { ...data, id: row.id };
  };

  const postAction = (message: string, isError: boolean) => {
    showSnackbar(message, isError);
  };

  return (
    <>
      <PageTitle
        icon={<HolidayIcon />}
        title="Bank Holiday Management"
        testID={TEST_ID}
        action={{
          label: "Add Holiday",
          icon: <AddIcon />,
          onClick: () =>
            showCreateModal({
              bankHolidays: items,
            }),
        }}
      />

      <FilterBar
        dates={{
          value: dates,
          onChange: setDates,
          type: "range",
          inputFormat: "yyyy-MM-dd",
        }}
        country={{
          value: country,
          onChange: (val) => {
            setCountry(val as Country);
          },
        }}
        testID="BankHoliday"
      />

      <DataGrid
        columns={columns({
          showUpdateModal,
          showDeleteModal,
          bankHolidays: items ?? [],
        })}
        data={items || []}
        emptyState={{
          title: "Bank Holidays",
          description: "Bank Holidays will appear here for you.",
          testID: TEST_ID,
        }}
        errorState={{
          isError,
          onClick: refetch,
        }}
        loading={isPending}
        sortModel={[
          {
            field: "date",
            sort: "asc",
          },
        ]}
        testID={TEST_ID}
        disableColumnMenu
        hideExport
        hideFooterPagination
        disableSelectionOnClick
        hideColumnSelector
        processRowUpdate={handleRowUpdate}
        columnVisibility={isMd ? hiddenColumnsMobile : {}}
      />
    </>
  );
};
